class OralOfferSelector extends HTMLElement {
  connectedCallback() {
    this.root = this.closest("oral-configurator");
    this.options = Array.from(this.querySelectorAll('input[type="radio"][data-offer-choice]'));
    this.quantity = this.querySelector("[data-offer-quantity]");
    this.total = this.querySelector("[data-offer-total]");
    this.contents = this.querySelector("[data-offer-contents]");
    this.orderState = this.querySelector("[data-offer-order-state]");
    this.quantityButtons = Array.from(this.querySelectorAll("[data-quantity-change]"));
    this.continueLinks = Array.from(this.querySelectorAll("[data-offer-continue-for]"));
    this.pairPurchase = this.querySelector("[data-pair-purchase]");
    this.sticky = this.querySelector("[data-offer-sticky]");
    this.stickyLabel = this.querySelector("[data-offer-sticky-label]");
    this.stickyPrice = this.querySelector("[data-offer-sticky-price]");
    this.stickyButton = this.querySelector("[data-offer-sticky-submit]");
    this.primary = this.querySelector("[data-offer-primary]");
    this.primaryButton = this.querySelector("[data-offer-primary-submit]");
    this.nativeQuantities = Array.from(this.root?.querySelectorAll('.oral-selector input[name="quantity"]') || []);
    this.nativeVariants = Array.from(this.root?.querySelectorAll('[data-edition-panel] [data-variant-select]') || []);
    if (!this.root || !this.options.length || !this.quantity || !this.total || !this.contents || !this.orderState) return;

    this.onOptionChange = this.onOptionChange.bind(this);
    this.onRootClick = this.onRootClick.bind(this);
    this.onQuantityInput = this.onQuantityInput.bind(this);
    this.onQuantityClick = this.onQuantityClick.bind(this);
    this.onNativeQuantityInput = this.onNativeQuantityInput.bind(this);
    this.onNativeVariantChange = this.onNativeVariantChange.bind(this);
    this.onStickyClick = this.onStickyClick.bind(this);
    this.onPrimaryClick = this.onPrimaryClick.bind(this);
    this.options.forEach((option) => option.addEventListener("change", this.onOptionChange));
    this.root.addEventListener("click", this.onRootClick);
    this.quantity.addEventListener("input", this.onQuantityInput);
    this.quantityButtons.forEach((button) => button.addEventListener("click", this.onQuantityClick));
    this.nativeQuantities.forEach((input) => {
      input.addEventListener("input", this.onNativeQuantityInput);
      input.addEventListener("change", this.onNativeQuantityInput);
    });
    this.nativeVariants.forEach((select) => select.addEventListener("change", this.onNativeVariantChange));
    this.stickyButton?.addEventListener("click", this.onStickyClick);
    this.primaryButton?.addEventListener("click", this.onPrimaryClick);

    const initial = this.options.find((option) => option.checked && this.dataset[`${option.value}Ready`] === "true")
      || this.options.find((option) => this.dataset[`${option.value}Ready`] === "true");
    if (initial) {
      initial.checked = true;
      this.selectOffer(initial.value);
    } else this.updateSticky(false);
    this.dataset.offerEnhanced = "true";
    this.root.dataset.offerEnhanced = "true";
  }

  disconnectedCallback() {
    this.options?.forEach((option) => option.removeEventListener("change", this.onOptionChange));
    this.root?.removeEventListener("click", this.onRootClick);
    this.quantity?.removeEventListener("input", this.onQuantityInput);
    this.quantityButtons?.forEach((button) => button.removeEventListener("click", this.onQuantityClick));
    this.nativeQuantities?.forEach((input) => {
      input.removeEventListener("input", this.onNativeQuantityInput);
      input.removeEventListener("change", this.onNativeQuantityInput);
    });
    this.nativeVariants?.forEach((select) => select.removeEventListener("change", this.onNativeVariantChange));
    this.stickyButton?.removeEventListener("click", this.onStickyClick);
    this.primaryButton?.removeEventListener("click", this.onPrimaryClick);
    delete this.dataset.offerEnhanced;
    if (this.root) delete this.root.dataset.offerEnhanced;
  }

  onOptionChange(event) {
    if (!event.currentTarget.checked) return;
    this.selectOffer(event.currentTarget.value);
  }

  onRootClick(event) {
    const control = event.target.closest?.("[data-edition-select], [data-priority-control]");
    if (!control || this.contains(control)) return;
    const edition = control.dataset.editionSelect || control.dataset.edition;
    if (edition !== "silver" && edition !== "gold") return;
    this.selectOffer(edition);
    if (control.matches("[data-edition-select]")) this.revealDecisionModule();
  }

  revealDecisionModule() {
    const reducedMotion = this.ownerDocument?.defaultView?.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    this.scrollIntoView?.({ behavior: reducedMotion ? "instant" : "smooth", block: "start" });
    this.focus({ preventScroll: true });
  }

  onQuantityInput() {
    this.quantity.value = String(this.normalizedQuantity());
    this.updateSummary();
  }

  onQuantityClick(event) {
    if (this.offer === "duo") return;
    const delta = Number(event.currentTarget.dataset.quantityChange);
    const rule = this.quantityRule(this.offer);
    this.quantity.value = String(this.normalizedQuantity(this.normalizedQuantity() + delta * rule.step));
    this.updateSummary();
  }

  onNativeQuantityInput(event) {
    const edition = event.currentTarget.id.match(/^OralQuantity-(silver|gold)-/)?.[1];
    if (edition !== this.offer) return;
    this.quantity.value = event.currentTarget.value;
    this.quantity.value = String(this.normalizedQuantity());
    this.updateSummary();
  }

  onNativeVariantChange(event) {
    const edition = event.currentTarget.closest("[data-edition-panel]")?.dataset.editionPanel;
    if (edition !== "silver" && edition !== "gold") return;
    const offer = this.querySelector(`[data-offer-option="${edition}"]`);
    const selected = event.currentTarget.selectedOptions[0];
    if (!offer || !selected) return;
    const cents = Number(selected.dataset.priceCents);
    const eligible = selected.dataset.orderEligible === "true"
      && Number.isSafeInteger(cents) && cents > 0 && !!selected.dataset.formattedPrice;
    this.dataset[`${edition}Ready`] = String(eligible);
    if (eligible) {
      offer.dataset.unitCents = String(cents);
      offer.querySelector("[data-offer-price]").textContent = selected.dataset.formattedPrice;
    } else {
      delete offer.dataset.unitCents;
      offer.querySelector("[data-offer-price]").textContent = "Not yet available";
    }
    this.applyQuantityRule(edition, false);
    if (this.offer === edition) {
      this.updateSummary();
      this.updateAvailability();
    }
  }

  onStickyClick() {
    this.activateSelectedPurchase();
  }

  onPrimaryClick() {
    this.activateSelectedPurchase();
  }

  activateSelectedPurchase() {
    if (this.dataset[`${this.offer}Ready`] !== "true") return;
    const target = this.offer === "duo"
      ? this.root.querySelector("[data-pair-add]")
      : this.root.querySelector(`[data-edition-panel="${this.offer}"] [data-add-button]`);
    if (!target || target.disabled || target.hidden) return;
    target.click();
  }

  selectOffer(offer) {
    if (!this.options.some((option) => option.value === offer)) return;
    this.offer = offer;
    this.options.forEach((option) => { option.checked = option.value === offer; });

    const isDuo = offer === "duo";
    if (isDuo) this.quantity.value = "1";
    this.quantity.readOnly = isDuo;
    if (isDuo) {
      this.quantity.min = "1";
      this.quantity.max = "1";
      this.quantity.step = "1";
    } else this.applyQuantityRule(offer, true);
    this.quantityButtons.forEach((button) => { button.disabled = isDuo; });

    this.syncHero();
    this.updateSummary();
    this.updateAvailability();
  }

  syncHero() {
    if (this.offer === "silver" || this.offer === "gold") {
      if (typeof this.root.setActiveEdition === "function") this.root.setActiveEdition(this.offer);
      else this.setEditionDomState(this.offer);
      return;
    }

    this.root.dataset.activeEdition = "duo";
    this.root.querySelectorAll("[data-oral-visual]").forEach((visual) => { visual.dataset.active = "true"; });
    this.root.querySelectorAll("[data-edition-select], [data-priority-control]").forEach((button) => {
      button.setAttribute("aria-pressed", "false");
    });
    this.root.querySelectorAll('.oral-selector input[name^="oral-edition-"]').forEach((radio) => { radio.checked = false; });
    this.nativeQuantities.forEach((input) => { input.value = "1"; });
    this.root.querySelectorAll("[data-edition-panel]").forEach((panel) => { panel.dataset.active = "false"; });
  }

  setEditionDomState(edition) {
    this.root.dataset.activeEdition = edition;
    this.root.querySelectorAll("[data-oral-visual]").forEach((visual) => {
      visual.dataset.active = String(visual.dataset.edition === edition);
    });
    this.root.querySelectorAll("[data-edition-panel]").forEach((panel) => {
      panel.dataset.active = String(panel.dataset.editionPanel === edition);
    });
  }

  nativeQuantity(edition) {
    return this.nativeQuantities.find((input) => input.id.startsWith(`OralQuantity-${edition}-`));
  }

  quantityRule(edition) {
    const selected = this.root.querySelector(`[data-edition-panel="${edition}"] [data-variant-select]`)?.selectedOptions[0];
    const native = this.nativeQuantity(edition);
    const min = Number(selected?.dataset.quantityMin || native?.min || 1);
    const max = Number(selected?.dataset.quantityMax || native?.max || 0);
    const step = Number(selected?.dataset.quantityStep || native?.step || 1);
    return {
      min: Number.isSafeInteger(min) && min > 0 ? min : 1,
      max: Number.isSafeInteger(max) && max > 0 ? max : null,
      step: Number.isSafeInteger(step) && step > 0 ? step : 1,
    };
  }

  applyQuantityRule(edition, reset) {
    const rule = this.quantityRule(edition);
    const native = this.nativeQuantity(edition);
    if (native) {
      native.min = String(rule.min);
      native.max = rule.max === null ? "" : String(rule.max);
      native.step = String(rule.step);
      if (reset) native.value = String(rule.min);
    }
    if (this.offer !== edition) return;
    this.quantity.min = String(rule.min);
    this.quantity.max = rule.max === null ? "" : String(rule.max);
    this.quantity.step = String(rule.step);
    this.quantity.value = String(reset ? rule.min : this.normalizedQuantity());
  }

  normalizedQuantity(raw = this.quantity.value) {
    if (this.offer === "duo") return 1;
    const rule = this.quantityRule(this.offer);
    const value = Number(raw);
    if (!Number.isSafeInteger(value)) return rule.min;
    const bounded = Math.min(rule.max ?? Number.MAX_SAFE_INTEGER, Math.max(rule.min, value));
    return rule.min + Math.floor((bounded - rule.min) / rule.step) * rule.step;
  }

  updateSummary() {
    const option = this.querySelector(`[data-offer-option="${this.offer}"]`);
    if (!option) return;
    const ready = this.dataset[`${this.offer}Ready`] === "true";
    const summary = this.querySelector(".oral-offer-selector__summary");
    if (summary) summary.hidden = !ready;
    if (!ready) {
      this.setRootSummary(`${this.offer === "gold" ? "Gold" : "Silver"} is not currently available to order.`);
      this.updateSticky(false);
      return;
    }
    const quantity = this.normalizedQuantity();
    const unitCents = Number(option.dataset.unitCents);
    if (!Number.isSafeInteger(unitCents) || unitCents <= 0) {
      this.updateSticky(false);
      return;
    }
    const currency = this.dataset.currencyCode;
    let formattedTotal;
    try {
      formattedTotal = `${new Intl.NumberFormat("en-US", { style: "currency", currency }).format((unitCents * quantity) / 100)} ${currency}`;
    } catch {
      formattedTotal = "View total in bag";
    }
    this.total.textContent = formattedTotal;

    if (this.offer === "duo") {
      this.contents.textContent = this.fixedPairContents();
      this.setRootSummary(`Duo selected: one Silver + one Gold · ${formattedTotal}.`);
      this.updateSticky(true);
      return;
    }

    const name = this.offer === "gold" ? "Gold" : "Silver";
    this.contents.textContent = `${quantity} ${name} Edition${quantity === 1 ? "" : "s"}`;
    this.setRootSummary(`${quantity} ${name} Edition${quantity === 1 ? "" : "s"} selected · ${formattedTotal}.`);
    const nativeQuantity = this.nativeQuantity(this.offer);
    if (nativeQuantity) nativeQuantity.value = String(quantity);
    this.updateSticky(true);
  }

  fixedPairContents() {
    const pair = this.pairPurchase;
    const included = ["silver", "gold"].map((edition) => {
      const select = this.nativeVariants.find((variant) => variant.closest("[data-edition-panel]")?.dataset.editionPanel === edition);
      const id = pair?.dataset[`${edition}Variant`];
      const option = Array.from(select?.options || []).find((variant) => variant.value === id);
      return option?.dataset.formattedPrice ? `${edition === "silver" ? "Silver" : "Gold"} ${option.dataset.formattedPrice}` : null;
    });
    return included.every(Boolean)
      ? `Fixed Duo includes ${included.join(" + ")}; changing a single option does not change these included variants.`
      : "Fixed Duo includes one Silver + one Gold; changing a single option does not change these included variants.";
  }

  setRootSummary(message) {
    const summary = this.root.querySelector("[data-selected-summary]");
    if (!summary) return;
    const ready = this.dataset[`${this.offer}Ready`] === "true";
    summary.hidden = !ready;
    if (!ready) {
      summary.textContent = "";
      summary.setAttribute("aria-disabled", "true");
      return;
    }
    summary.textContent = message;
    summary.setAttribute("aria-disabled", "false");
  }

  updateAvailability() {
    const ready = this.dataset[`${this.offer}Ready`] === "true";
    if (ready) {
      this.orderState.textContent = this.offer === "duo"
        ? "The Duo is ready to order below."
        : `${this.offer === "gold" ? "Gold" : "Silver"} is ready to order below.`;
    } else {
      this.orderState.textContent = `${this.offer === "gold" ? "Gold" : "Silver"} is not currently available to order.`;
    }
    this.continueLinks.forEach((link) => { link.hidden = !ready || link.dataset.offerContinueFor !== this.offer; });
    if (this.pairPurchase) this.pairPurchase.hidden = !(ready && this.offer === "duo");
    this.updateSticky(ready);
  }

  updateSticky(ready) {
    if (this.root) this.root.dataset.stickyPurchase = String(ready);
    if (this.sticky) {
      this.sticky.hidden = !ready;
      this.sticky.dataset.stickyFor = ready ? this.offer : "none";
    }
    if (this.primary) this.primary.hidden = !ready;
    if (this.stickyButton) this.stickyButton.disabled = !ready;
    if (this.primaryButton) {
      this.primaryButton.hidden = !ready;
      this.primaryButton.disabled = !ready;
    }
    if (!ready) {
      if (this.stickyLabel) this.stickyLabel.textContent = "Choose an edition";
      if (this.stickyPrice) this.stickyPrice.textContent = "";
      for (const button of [this.stickyButton, this.primaryButton]) {
        if (!button) continue;
        button.textContent = "";
        button.removeAttribute("aria-label");
      }
      return;
    }
    const label = this.offer === "duo" ? "Silver + Gold Duo" : `${this.offer === "gold" ? "Gold" : "Silver"} Edition`;
    const action = this.offer === "duo" ? "Add Silver + Gold to bag" : `Add ${this.offer === "gold" ? "Gold" : "Silver"} to bag`;
    if (this.stickyLabel) this.stickyLabel.textContent = label;
    if (this.stickyPrice) this.stickyPrice.textContent = this.total?.textContent || "";
    for (const button of [this.stickyButton, this.primaryButton]) {
      if (!button) continue;
      button.textContent = action;
      button.setAttribute("aria-label", action);
    }
  }
}

if (!customElements.get("oral-offer-selector")) customElements.define("oral-offer-selector", OralOfferSelector);
